import React, { useMemo } from 'react'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { formatDate } from '@/v2/utils/dateHelpers'
import { TableCard, EmptyState, EmptyStateFilters } from '../styles'
import { METHOD_LABELS, PAYMENT_STATUS_LABELS } from '../constants'
import PaymentRow from './PaymentRow'

// The app theme defines `palette.divider` as an object ({ main: '#EEEDED' }),
// but MUI's TableCell expects a string color and crashes in `alpha(divider, 1)`.
// Patch divider to a string for this subtree only.
const useTableCellTheme = () => {
  const base = useTheme()
  return useMemo(
    () => ({
      ...base,
      palette: {
        ...base.palette,
        divider: base.palette.divider?.main || '#EEEDED',
      },
    }),
    [base]
  )
}

const COLUMN_HEADERS = [
  '',
  'Date',
  'Patient',
  'Contract',
  'Method',
  'Base',
  'Patient fee',
  'Total',
  'Payment',
  'Receipt',
]

const RIGHT_ALIGNED = new Set(['Base', 'Patient fee', 'Total'])

// "No payments match these filters" doesn't say which ones, and the date
// popover covers the Method and Payment status selects while it's open — so a
// filter set minutes ago and forgotten is invisible exactly when you're trying
// to work out why the table is empty. Name them instead.
const activeFilters = ({ startDate, endDate, method, paymentStatus, search }) =>
  [
    startDate &&
      endDate && [
        'Date',
        `${formatDate(startDate, 'MMM dd, yyyy')} – ${formatDate(
          endDate,
          'MMM dd, yyyy'
        )}`,
      ],
    method && ['Method', METHOD_LABELS[method] || method],
    paymentStatus && [
      'Payment status',
      PAYMENT_STATUS_LABELS[paymentStatus] || paymentStatus,
    ],
    search && ['Search', `"${search}"`],
  ].filter(Boolean)

const PaymentsTable = ({
  payments,
  pagination,
  filters,
  onPageChange,
  onPerPageChange,
}) => {
  const patchedTheme = useTableCellTheme()

  // The API paginates: `payments` is already the current page and
  // `pagination` carries the 1-based currentPage plus the total record count.
  const { currentPage, perPage, totalRecords } = pagination

  const handleChangePage = (_event, newPage) => onPageChange(newPage + 1)
  const handleChangeRowsPerPage = event =>
    onPerPageChange(parseInt(event.target.value, 10))

  if (payments.length === 0) {
    return (
      <TableCard>
        <EmptyState>
          <div>No payments match these filters</div>
          <EmptyStateFilters>
            {activeFilters(filters || {}).map(([label, value]) => (
              <span key={label}>
                {label}: <b>{value}</b>
              </span>
            ))}
          </EmptyStateFilters>
        </EmptyState>
      </TableCard>
    )
  }

  return (
    <ThemeProvider theme={patchedTheme}>
      <TableCard>
        <TableContainer>
          <Table size="small" aria-label="payments table">
            <TableHead>
              <TableRow>
                {COLUMN_HEADERS.map(header => (
                  <TableCell
                    key={header || 'expand-toggle'}
                    align={RIGHT_ALIGNED.has(header) ? 'right' : 'left'}
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: '#7D7D7D',
                      background: '#f9fafb',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map(payment => (
                <PaymentRow key={payment.id} payment={payment} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalRecords}
          page={currentPage - 1}
          onPageChange={handleChangePage}
          rowsPerPage={perPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableCard>
    </ThemeProvider>
  )
}

export default PaymentsTable
